import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { PageBody, Row, Cell, ProgressIndicator, Button, Icons, SecurityHelper } from 'sarsaparilla';
import showLoginModal from '../actions/loginModal';
import LoginRequest from './LoginRequest';
import OrderDetails from '../components/OrderDetails';
import {
  fetchFacilities,
  fetchPassTypesByFacility,
  fetchReservation,
  createReservation,
  updateReservation,
  removeReservation
} from '../actions';
import { facilitiesSelectors, passTypesSelectors, reservationSelectors, isLoading, isLoaded } from '../reducers';
import { FacilityProps, PassTypeProps, ReservationDefaults, ReservationProps } from '../props';

const propTypes = {
  location: PropTypes.shape({
    query: PropTypes.shape({
      fid: PropTypes.string,
      ptid: PropTypes.string,
      rid: PropTypes.string
    })
  }),
  account: PropTypes.object,
  facilities: PropTypes.arrayOf(FacilityProps),
  passTypes: PropTypes.arrayOf(PassTypeProps),
  reservation: ReservationProps,
  loaded: PropTypes.objectOf(PropTypes.bool),
  loading: PropTypes.objectOf(PropTypes.bool),
  fetchFacilities: PropTypes.func.isRequired,
  fetchPassTypes: PropTypes.func.isRequired,
  fetchReservation: PropTypes.func.isRequired,
  createReservation: PropTypes.func.isRequired,
  updateReservation: PropTypes.func.isRequired,
  removeReservation: PropTypes.func.isRequired,
  showLoginModal: PropTypes.func.isRequired
};

const defaultProps = {};

const message = 'Before you buy a pass, you must sign in.';

export class OrderDetailsPage extends PureComponent {
  constructor(props) {
    super(props);

    const { fid, ptid } = this.props.location.query;

    this.state = {
      reservation: Object.assign({}, ReservationDefaults, { facility_id: fid, pass_type_id: ptid }),
      reservationIsValid: false
    };

    this.loadPageData = this.loadPageData.bind(this);
    this.updatePassTypes = this.updatePassTypes.bind(this);

    this.onFacilityChanged = this.onFacilityChanged.bind(this);
    this.onPassTypeChanged = this.onPassTypeChanged.bind(this);
    this.onReservationChanged = this.onReservationChanged.bind(this);

    this.saveReservation = this.saveReservation.bind(this);
    this.deleteReservation = this.deleteReservation.bind(this);

    this.renderOrderDetails = this.renderOrderDetails.bind(this);
  }

  componentDidMount() {
    const { loading, loaded, location: { query } } = this.props;
    const loggedIn = SecurityHelper.isLoggedIn();

    if (!loggedIn) {
      this.props.showLoginModal();
      return;
    }

    this.loadPageData(loading, loaded, query);
  }

  componentWillReceiveProps(nextProps) {
    const { loading, loaded, location: { query } } = nextProps;
    const newState = {};
    const loggedIn = SecurityHelper.isLoggedIn();

    if (nextProps.reservation !== this.props.reservation) {
      newState.reservation = nextProps.reservation;
    }

    if (loggedIn) {
      // Need to try to load data after a user has logged in.
      this.loadPageData(loading, loaded, query);
    }

    this.setState((prevState) => ({ ...prevState, ...newState }));
  }

  onFacilityChanged(facilityId) {
    this.setState((prevState) => ({
      ...prevState,
      reservation: { ...prevState.reservation, facility_id: facilityId }
    }), this.updatePassTypes);
  }

  onPassTypeChanged(passTypeId) {
    this.setState((prevState) => ({
      ...prevState,
      reservation: { ...prevState.reservation, pass_type_id: passTypeId }
    }));
  }

  onReservationChanged(reservation, reservationIsValid) {
    this.setState((prevState) => ({
      ...prevState,
      reservation: { ...prevState.reservation, ...reservation },
      reservationIsValid
    }));
  }

  loadPageData(loading, loaded, query) {
    if (!loading.facilities && !loaded.facilities) {
      this.props.fetchFacilities();
    }

    if ((query.fid || this.props.reservation.facility_id) && !loading.passTypes && !loaded.passTypes) {
      this.updatePassTypes();
    }

    if (query.rid && !loading.reservation && !loaded.reservation) {
      this.props.fetchReservation(query.rid);
    }
  }

  updatePassTypes() {
    this.props.fetchPassTypes(this.state.reservation.facility_id);
  }

  async saveReservation() {
    const { reservation } = this.state;

    let action = this.props.createReservation;

    if (reservation.id !== null) {
      action = this.props.updateReservation;
    }

    try {
      await action(reservation);
      window.location.assign('/cart');
    }
    catch (e) {
      // TODO Handle errors
    }
  }

  async deleteReservation() {
    const { reservation } = this.props;

    try {
      await this.props.removeReservation(reservation.id);
      window.location.assign('/cart');
    }
    catch (e) {
      // TODO Handle errors
    }
  }

  renderOrderDetails() {
    const { facilities, passTypes, loading, loaded } = this.props;
    const { reservation, reservationIsValid } = this.state;
    const canDelete = reservation.id !== null;

    return (
      <OrderDetails
        facilityId={reservation.facility_id}
        facilities={facilities}
        passTypeId={reservation.pass_type_id}
        passTypes={passTypes}
        reservation={reservation}
        loading={loading}
        loaded={loaded}
        onFacilityChange={this.onFacilityChanged}
        onPassTypeChange={this.onPassTypeChanged}
        onReservationChange={this.onReservationChanged}>
        <Row>
          <Cell>
            <Button size={'large'} onClick={this.saveReservation} disabled={!reservationIsValid}>
              Proceed To Cart
            </Button>
            {canDelete ? <Button mode={'link'} onClick={this.deleteReservation}>
              <Icons.IconDelete alt="Remove item from cart" />
              Remove From Cart
            </Button> : null}
          </Cell>
        </Row>
      </OrderDetails>
    );
  }

  render() {
    const steps = ['Order Details', 'Review Cart', 'Payment', 'Confirmation'];
    const isLoggedIn = this.props.account != null;

    return (
      <PageBody>
        <Row className="parkpass-order-details-heading">
          <Cell>
            <ProgressIndicator steps={steps} activeStep={0} />
          </Cell>
        </Row>
        {isLoggedIn ? this.renderOrderDetails() : <LoginRequest message={message} />}
      </PageBody>
    );
  }
}

OrderDetailsPage.propTypes = propTypes;
OrderDetailsPage.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  account: state.auth && state.auth.account && state.auth.account.account,
  facilities: facilitiesSelectors.getFacilities(state),
  passTypes: passTypesSelectors.getPassTypes(state),
  loading: isLoading(state, 'reservation', 'facilities', 'passTypes'),
  loaded: isLoaded(state, 'reservation', 'facilities', 'passTypes'),
  reservation: reservationSelectors.getReservation(state)
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchFacilities,
  fetchPassTypes: fetchPassTypesByFacility,
  fetchReservation,
  removeReservation,
  createReservation,
  updateReservation,
  showLoginModal
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPage);
